import { Injectable, Signal, inject, signal, WritableSignal } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { EventInput } from '@fullcalendar/core';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { HttpService } from 'src/app/core/http/http.service';
import { IScheduleDataModel, IScheduleModel } from 'src/app/core/models/schedule.model';
import { LocalStorageService } from 'src/app/core/services/local-storage/local-storage.service';
import { getAuthStateSelect } from 'src/app/core/store/auth/auth.selector';
import { LoadingService } from 'src/app/shared/components/loading/loading.service';
import { EnumPartialRoutes } from 'src/app/shared/enums/enums';
import { deepCloning } from 'src/app/shared/utils/functions';


const parseToSelectOption = (i: string) => ({ label: i, value: i });

@Injectable({
  providedIn: 'root'
})
export class ScheduleService {
  public patients = [].map(parseToSelectOption);
  public types = [].map(parseToSelectOption);
  public unities = [].map(parseToSelectOption);
  public events: WritableSignal<EventInput[]> = signal<EventInput[]>([]);
  public originalData: IScheduleDataModel[] = [];
  public waitingRoomData: IScheduleDataModel[] = [];
  public showingDataSchedule: IScheduleDataModel[] = [];
  public selectedDate: Date = new Date();
  public currenDate: Date = new Date();
  // forms
  registrationForm = this.fb.group({
    patientSelected: 'Todos pacientes',
    typeSelected: 'Todos tipos',
    unitySelected: 'Todas unidades',
  });
  loadedData = false;
  interval: any;

  private store = inject(Store);

  constructor(
    private httpService: HttpService,
    private localStorageService: LocalStorageService,
    private router: Router,
    private loadingService: LoadingService,
    public fb: FormBuilder,
  ) {
    this.registrationForm.valueChanges.subscribe(changed => {
      const patient = changed.patientSelected == 'Todos pacientes' ? undefined : changed.patientSelected;
      const type = changed.typeSelected == 'Todos tipos' ? undefined : changed.typeSelected;
      const unity = changed.unitySelected == 'Todas unidades' ? undefined : changed.unitySelected;
      this.loadingService.show('schedule');
      this.router.navigate([EnumPartialRoutes.SCHEDULE]);

      setTimeout(() => {
        this.applyFilter(patient, type, unity);
      }, 500);

    });

    // this.store.select(getAuthStateSelect).subscribe( state => {
    //   if (state.user == null) {
    //     console.log('clearing')
    //     this.clearInternalState()
    //   }
    // } )
  }

  clearInternalState() {
    this.patients = [];
    this.types = [];
    this.unities = [];
    this.events.set([]);
    this.originalData = [];
    this.waitingRoomData = [];
    this.showingDataSchedule = [];
  }

  refreshPage() {
    this.interval = setInterval(() => {
      // this.router.navigate([EnumPartialRoutes.SCHEDULE]);
      this.callAPI();
      this.callWaitingRoom();
    }, 60000);
  }

  refreshEventsColor(): void {
    if (this.events().length != 0) {
      this.events.update((value) =>
        value.map((event) => ({
          ...event,
          backgroundColor: this.eventBackgroundColor(event),
          borderColor: this.eventBackgroundColor(event)
        }))
      )
    }
  }

  async getAppointments(practitionerId: string, startDate: string, endDate: string): Promise<IScheduleModel> {
    const result = await this.httpService.req('schedule', 'patient', {
      apiVersion: 2,
      loaderKey: 'schedule',
      params: {
        practitionerId,
        startDate,
        endDate,
        lg: 'pt-BR'
      },
      method: 'get'
    }) as any;
    return result;
  }

  async callAPI(): Promise<boolean> {
    this.loadedData = false;
    this.loadingService.show('schedule');
    const user = this.localStorageService.get('user');
    this.originalData = [];
    this.showingDataSchedule = [];

    return this.getAppointments(user.practitionerId, this.selectedDate.toISOString().replace(/T.*$/, ''), this.selectedDate.toISOString().replace(/T.*$/, ''))
      .then((value) => {
        this.patients = [{ label: "Todos pacientes", value: "Todos pacientes" }];
        this.types = [{ label: "Todos tipos", value: "Todos tipos" }];
        this.unities = [{ label: "Todas unidades", value: "Todas unidades" }];
        this.events.set([]);

        this.originalData = deepCloning(value.result);
        this.showingDataSchedule = deepCloning(value.result)
        this.showingDataSchedule = this.showingDataSchedule.filter((appointments) => {
          return !appointments.patientName.includes('B * L * O * Q * U * E * A * D * O')
        });

        this.events.set(
          value.result.filter(
            (appointments) => {
              return !appointments.patientName.includes('B * L * O * Q * U * E * A * D * O')
            }
          ).map((schedule, index) => {
            this.insertFilters(schedule);
            return {
              id: index.toString(),
              title: '',
              start: schedule.appointmentStartDate + 'T' + schedule.appointmentStartHour,
              end: schedule.appointmentEndDate + 'T' + schedule.appointmentEndHour,
              backgroundColor: this.eventBackgroundColor(schedule),
              borderColor: this.eventBackgroundColor(schedule),
            };
          }));

        this.loadedData = true;
        this.router.navigate([EnumPartialRoutes.SCHEDULE_DAY_VIEW]);
        this.loadingService.hide('schedule');
        return true;
      });
  }

  async callWaitingRoom(): Promise<boolean> {
    const user = this.localStorageService.get('user');
    this.waitingRoomData = [];
    return this.getAppointments(user.practitionerId, moment().toISOString().replace(/T.*$/, ''), moment().toISOString().replace(/T.*$/, ''))
      .then((result) => {

        if (result.result.length > 0) {
          this.waitingRoomData = result.result.filter((appointment) => {
            return appointment.waitingRoom && moment(appointment.appointmentStartDate).isSame(moment().format('YYYY-MM-DD')) && this.showOnWaitingRoom(appointment.myclinicStatus);
          });
        }
        return true;
      });
  }

  insertFilters(schedule: IScheduleDataModel) {
    if (this.patients.findIndex(value => value.value == schedule.patientName) == -1) {
      this.patients.push({ value: schedule.patientName, label: schedule.patientName });
    }

    if (this.types.findIndex(value => value.value == schedule.appointmentType) == -1) {
      this.types.push({ value: schedule.appointmentType, label: schedule.appointmentType });
    }

    if (this.unities.findIndex(value => value.value == schedule.clinic) == -1) {
      this.unities.push({ value: schedule.clinic, label: schedule.clinic });
    }
  }

  applyFilter(
    patient: string | null | undefined,
    type: string | null | undefined,
    unity: string | null | undefined,
  ) {
    this.showingDataSchedule = [];
    this.events.set([]);
    if (patient == undefined && type == undefined && unity == undefined) {
      this.patients = [];
      this.types = [];
      this.unities = [];
      this.patients = [{ label: "Todos pacientes", value: "Todos pacientes" }];
      this.types = [{ label: "Todos tipos", value: "Todos tipos" }];
      this.unities = [{ label: "Todas unidades", value: "Todas unidades" }];
      this.originalData.forEach((schedule) => {
        this.insertFilters(schedule);
      });
      this.showingDataSchedule = deepCloning(this.originalData);
      this.events.set(this.showingDataSchedule.map((value) => {
        return {
          id: value.patientId,
          title: '',
          start: value.appointmentStartDate + 'T' + value.appointmentStartHour,
          end: value.appointmentEndDate + 'T' + value.appointmentEndHour,
          backgroundColor: this.eventBackgroundColor(value),
          borderColor: this.eventBackgroundColor(value),
        }
      }))
    } else {
      this.events.set(
        this.originalData.filter((schedule) => {
          return ((patient && schedule.patientName.toLocaleLowerCase() == patient.toLocaleLowerCase()) ||
            (type && schedule.appointmentType.toLocaleLowerCase() == type.toLocaleLowerCase()) ||
            (unity && schedule.clinic.toLocaleLowerCase() == unity.toLocaleLowerCase()))
        }).map((schedule) => {
          this.showingDataSchedule.push(schedule);
          return {
            id: schedule.patientId,
            title: '',
            start: schedule.appointmentStartDate + 'T' + schedule.appointmentStartHour,
            end: schedule.appointmentEndDate + 'T' + schedule.appointmentEndHour,
            backgroundColor: this.eventBackgroundColor(schedule),
            borderColor: this.eventBackgroundColor(schedule),
          };

        }))
    }

    this.router.navigate([EnumPartialRoutes.SCHEDULE_DAY_VIEW]);
    this.loadingService.hide('schedule');
  }

  eventBackgroundColor(schedule: any): string {
    const status = schedule!.myclinicStatus;
    if (status && status !== '') {
      switch (status.toLowerCase()) {
        case 'marcado':
        case 'encaixe':
          return 'rgba(128, 128, 128, 0.3)';
        case 'confirmado':
          return 'rgba(51, 204, 153, 0.3)';
        case 'aguardando':
          return this.checkDelay(schedule)
        case 'aguardando encaixe':
        case 'em triagem':
          return 'rgba(255, 255, 0, 0.3)';
        case 'em consulta':
          return 'rgba(39, 113, 240, 0.2)';
        case 'efetivado':
        case 'atendido':
          return 'rgba(0, 128, 0, 0.2)';
        case 'faltou':
        case 'cancelado':
        case 'bloqueado':
          return 'rgba(128, 0, 128, 0.2)';
        default:
          return 'rgba(128, 128, 128, 0.2)';
      }
    }
    return 'rgba(128, 128, 128, 0.2)';
  }

  showOnWaitingRoom(status: string): boolean {
    switch (status.toLowerCase()) {
      case 'aguardando':
      case 'aguardando encaixe':
      case 'em consulta':
      case 'atendido':
      case 'efetivado':
        return true;

      case 'marcado':
      case 'encaixe':
      case 'confirmado':
      case 'em triagem':
      case 'faltou':
      case 'cancelado':
      case 'bloqueado':
      default:
        return false;
    }
  }

  checkDelay(appointment: IScheduleDataModel) {
    if (!appointment.arrivalHour || appointment.arrivalHour === '- -') {
      return 'rgba(128, 128, 128, 0.3)';
    }

    const bookedAppointmentDate = new Date(`${appointment.appointmentStartDate}T${appointment.appointmentStartHour}-03:00`);
    const arrivalHour = new Date(appointment.arrivalHour);
    const nowDateHour = new Date();

    let waitingTime;

    if (bookedAppointmentDate.getTime() >= arrivalHour.getTime()) {
      waitingTime = nowDateHour.getTime() - bookedAppointmentDate.getTime();
    }
    else if (bookedAppointmentDate.getTime() < arrivalHour.getTime()) {
      waitingTime = nowDateHour.getTime() - arrivalHour.getTime();
    }
    else {
      return 'rgba(255, 255, 0, 0.3)';
    }

    const diffMinutes = waitingTime / 60000;

    if (diffMinutes <= 0) {
      return 'rgba(255, 255, 0, 0.3)'; // Adiantado
    } else if (diffMinutes > 0 && diffMinutes < 15) {
      return 'rgba(255, 165, 0, 0.3)'; // Pequeno atraso (menos de 15 minutos)
    } else {
      return 'rgba(255, 0, 0, 0.1)'; // Atraso significativo (15 minutos ou mais)
    }
  }

  async startAppointment(appointmentId: string): Promise<IScheduleModel> {
    this.loadingService.show('schedule');
    let result;

    try {
      result = await this.httpService.req('updateStartOrEndAppointment', 'patient', {
        loaderKey: 'schedule',
        apiVersion: 2,
        body: {
          appointmentId,
          status: 'end',
          dateTime: moment().utc(true).local().format()
        },
        method: 'put'
      }) as any;
    }
    catch (error) {
      this.loadingService.hide('schedule');
      throw error;
    }

    this.loadingService.hide('schedule');
    return result;
  }
}
